import { useMemo } from "react";
import jwt_decode from "jwt-decode";
import {
  useAccounts,
  useFetchKibanaDashboards,
  useOrganization,
} from "./GetDataFromStore";
import accessToken from "../../../helpers/jwt-token-access/accessToken";

// Custom hook to get accessible user accounts
export const useAccessibleUserAccounts = () => {
  const accounts = useAccounts(); // Always call useAccounts

  const accessibleAccounts = useMemo(() => {
    let username = null;
    const authUser = localStorage.getItem("authUser");

    if (authUser) {
      try {
        const decodedToken = jwt_decode(JSON.parse(authUser).idToken);
        username = decodedToken.email;
      } catch (error) {
        console.error("Error decoding token:", error);
        return []; // Return an empty array if there's an error
      }
    }

    if (accounts && username) {
      const filteredAccounts = accounts.filter(
        (x) =>
          !x.accountNumber.includes("INIT00000001") &&
          !x.accountNumber.includes("TEST")
          // x.accountNumber.length === 12
      );

      return filteredAccounts.filter(
        (acct) =>
          acct.status === "ACTIVE" &&
          acct.listusr?.some((user) => user.userName === username)
      );
    }

    return []; // Return an empty array if conditions are not met
  }, [accounts]);

  return accessibleAccounts;
};

// Custom hook to check if user has active accounts
export const useHasActiveUserAccounts = () => {
  const accessibleAccounts = useAccessibleUserAccounts();
  return useMemo(() => accessibleAccounts.length > 0, [accessibleAccounts]);
};

// account with the oldest activation_date
export const useOldestActivatedAccount = () => {
  const accessibleAccounts = useAccessibleUserAccounts();

  const oldestAccount = useMemo(() => {
    if (!accessibleAccounts || accessibleAccounts.length === 0) {
      return null; // Return null if there are no accessible accounts
    }

    // Filter out accounts with null or undefined activation_date
    const accountsWithActivationDate = accessibleAccounts.filter(
      (account) => account.activation_date
    );

    if (accountsWithActivationDate.length === 0) {
      return null; // Return null if no accounts have an activation_date
    }

    // Sort accounts by activation_date in ascending order and return the first one
    return accountsWithActivationDate.reduce((oldest, current) => {
      const oldestDate = new Date(oldest.activation_date);
      const currentDate = new Date(current.activation_date);
      return currentDate < oldestDate ? current : oldest;
    });
  }, [accessibleAccounts]);

  return oldestAccount;
};

// Custom hook to check if Cost Insight is enabled
export const useIsCostInsightEnabledForOneUserAccount = () => {
  const accessibleAccounts = useAccessibleUserAccounts();

  return useMemo(() => {
    return accessibleAccounts.some((account) =>
      account?.subscription?.addons?.some(
        (addon) => addon.name === "Cost Insight" && addon.enabled
      )
    );
  }, [accessibleAccounts]);
};

// Custom hook to check if Cost Optimization is enabled
export const useIsCostOptimizationEnabledForOneUserAccount = () => {
  const accessibleAccounts = useAccessibleUserAccounts();

  return useMemo(() => {
    return accessibleAccounts.some((account) =>
      account?.subscription?.addons?.some(
        (addon) => addon.name === "Cost Optimization" && addon.enabled
      )
    );
  }, [accessibleAccounts]);
};

// Custom hook to check if Event Insight is enabled
export const useIsEventInsightEnabledForOneUserAccount = () => {
  const accessibleAccounts = useAccessibleUserAccounts();

  return useMemo(() => {
    return accessibleAccounts.some((account) =>
      account?.subscription?.addons?.some(
        (addon) => addon.name === "Event Insight" && addon.enabled
      )
    );
  }, [accessibleAccounts]);
};

function calculateRelativeTime(lastRunDate) {
  if (!lastRunDate) return "now-30d"; // Default fallback - removed /d suffix

  const lastRun = new Date(lastRunDate.replace(" ", "T") + ":00Z"); // Ensure ISO format
  const now = new Date();

  // Calculate difference in milliseconds and convert to days
  const diffDays = Math.round((now - lastRun) / (1000 * 60 * 60 * 24));

  // Ensure minimum 1-day difference
  return `now-${diffDays}d`; // Removed /d suffix
}

// Get Kibana Dashboard URL
export const useCreateKibanaDashboardURL = (
  dashboardKey,
  params = undefined
) => {
  console.log("🚀 ~ useCreateKibanaDashboardURL ~ dashboardKey:", dashboardKey);
  console.log("Params :: ", params);

  const { dashboards, isLoading, errorMessage } = useFetchKibanaDashboards();

  const dashboardId = useMemo(() => {
    if (!dashboards || isLoading || errorMessage) {
      return null;
    }

    // Find the specific dashboard ID using the provided dashboardKey
    return dashboards.find((dashboard) => dashboard.key === dashboardKey)
      ?.value;
  }, [dashboards, isLoading, errorMessage, dashboardKey]);

  console.log("🚀 ~ dashboardId ~ dashboardId:", dashboardId);

  const organization = useOrganization();
  console.log("🚀 ~ useCreateKibanaDashboardURL ~ organization:", organization);

  const dashboardURL = useMemo(() => {
    // Get orgcode - use mspOrgCode from selectedOrganization if CUSTOMER type, otherwise use organization.orgcode
    const isCustomerType = organization?.registrationType === "CUSTOMER";
    let orgcode = null;
    if (isCustomerType) {
      try {
        const selectedOrganizationStr = localStorage.getItem("selectedOrganization");
        if (selectedOrganizationStr) {
          const selectedOrganization = JSON.parse(selectedOrganizationStr);
          orgcode = selectedOrganization?.mspOrgCode;
        }
      } catch (error) {
        console.error("Error parsing selectedOrganization from localStorage:", error);
      }
    } else {
      orgcode = organization?.orgcode;
    }

    if (!dashboardId || !orgcode) {
      return null;
    }

    const token = accessToken();
    const VIEW_URL = process.env.REACT_APP_ELASTICSERACH;
    const SPACE_URL = orgcode.toLowerCase();

    const QUERY_PARAMS = `?authorization=${token}`;
    let URL_SUFFIX = process.env.REACT_APP_ELASTICSERACH_URL_SUFFIX;

    // Helper function to build account filters for CUSTOMER type organizations
    const buildCustomerAccountFilters = () => {
      const isCustomerType = organization?.registrationType === "CUSTOMER";

      // List of dashboards that should have customer account filters applied
      const customerFilterDashboards = [
        "cur_2_dashboard",
        "cur_2_dashboard_orignal_cost",
        "CUR_2_insight_Allocation",
        "Data Transfer",
        "RI/SP Dashboard",
        "EKS Cluster",
        "aws_connect_dashboard",
        "marketplace_dashboard",
      ];

      const shouldApplyFilter = customerFilterDashboards.includes(dashboardKey);

      if (
        !isCustomerType ||
        !shouldApplyFilter ||
        !organization?.customerAccountNumbers
      ) {
        return null;
      }

      // Parse customerAccountNumbers (string -> array)
      let accountNumbersArray = [];
      const raw = organization.customerAccountNumbers;
      try {
        let parsed;
        if (Array.isArray(raw)) {
          parsed = raw;
        } else if (typeof raw === "string") {
          try {
            parsed = JSON.parse(raw);
          } catch (e) {
            const inner = raw.replace(/^\s*\[|\]\s*$/g, "").trim();
            parsed = inner ? inner.split(",").map((s) => s.trim().replace(/^["']|["']$/g, "")) : [];
          }
        } else {
          parsed = [];
        }
        accountNumbersArray = Array.isArray(parsed)
          ? parsed.map((acc) => String(acc).trim()).filter((acc) => acc)
          : [];
      } catch (error) {
        console.error("Error parsing customerAccountNumbers:", error);
        return null;
      }

      if (accountNumbersArray.length === 0) {
        return null;
      }

      // Build filter
      let filterString = "";

      if (accountNumbersArray.length === 1) {
        const acc = accountNumbersArray[0];
        filterString = `(meta:(key:'line_item_usage_account_id.keyword',params:(query:'${acc}'),type:'phrase'),query:(match_phrase:(line_item_usage_account_id.keyword:'${acc}')))`;
      } else {
        // Multiple accounts -> OR condition
        filterString = `(meta:(key:'line_item_usage_account_id.keyword',params:(query:'${accountNumbersArray.join(
          " OR "
        )}'),type:'phrases'),query:(bool:(should:!(`;
        accountNumbersArray.forEach((acc, index) => {
          if (index > 0) filterString += ",";
          filterString += `(match_phrase:(line_item_usage_account_id.keyword:'${acc}'))`;
        });
        filterString += "))))";
      }

      console.log("🔍 Customer Account Numbers Filter Applied:", {
        accountNumbers: accountNumbersArray,
        filterString,
      });

      return filterString;
    };

    if (
      dashboardKey === "Event_Insight_New" ||
      dashboardKey === "MItre_event Insight" ||
      dashboardKey === "Event Detection POC" ||
      dashboardKey === "Landing Page" ||
      dashboardKey === "Resource Dashboard" ||
      dashboardKey === "Compliance" ||
      dashboardKey === "Asset Explorer" ||
      dashboardKey === "Resource Explorer_2" ||
      dashboardKey === "Security_2"
    ) {
      // URL_SUFFIX = process.env.REACT_APP_ELASTICSERACH_EVENT_INSIGHT_URL_SUFFIX;
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-24h,to:now))&_a=(timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true";
    } else if (
      dashboardKey === "CostOptimizationhub" ||
      dashboardKey === "Hub Details" ||
      dashboardKey === "Other Opportunities"
    ) {
      URL_SUFFIX =
        process.env.REACT_APP_ELASTICSERACH_COST_OPTIMIZATION_URL_SUFFIX;
    } else if (
      dashboardKey === "cur_2_dashboard" ||
      dashboardKey === "cur_2_dashboard_orignal_cost" ||
      dashboardKey === "CUR_2_insight_Allocation" ||
      dashboardKey === "Data Transfer" ||
      dashboardKey === "EKS Cluster" ||
      dashboardKey === "aws_connect_dashboard" ||
      dashboardKey === "marketplace_dashboard"
    ) {
      // Check if margin dates are provided for cost insight dashboards
      if (
        params?.startDate &&
        params?.endDate &&
        (dashboardKey === "cur_2_dashboard" ||
          dashboardKey === "cur_2_dashboard_orignal_cost" ||
          dashboardKey === "aws_connect_dashboard")
      ) {
        // helper: to ISO at start/end of day UTC
        // Convert date string to represent local midnight, then convert to UTC
        const toISOStart = (d) => {
          // Parse date components (YYYY-MM-DD)
          const [year, month, day] = d.split('-').map(Number);
          // Create date in local timezone (month is 0-indexed in JS)
          const dt = new Date(year, month - 1, day, 0, 0, 0, 0);
          // Convert local midnight to UTC ISO string
          return dt.toISOString();
        };
        const toISOEnd = (d) => {
          // Parse date components (YYYY-MM-DD)
          const [year, month, day] = d.split('-').map(Number);
          // Create date in local timezone at end of day (month is 0-indexed in JS)
          const dt = new Date(year, month - 1, day, 23, 59, 59, 999);
          // Convert local end of day to UTC ISO string
          return dt.toISOString();
        };

        // Convert dates to proper Kibana date format
        const fromRison = `'${toISOStart(params.startDate)}'`;
        const toRison = `'${toISOEnd(params.endDate)}'`;

        console.log("🔍 Cost Insight Dashboard Debug:", {
          dashboardKey,
          fromRison,
          toRison,
          orgCode: orgcode,
          registrationType: organization?.registrationType,
          customerAccountNumbers: organization?.customerAccountNumbers,
        });

        // Build URL suffix with only margin date filters (no org_code filter)
        let urlSuffix =
          "?embed=true&show-top-menu=false&show-query-input=false&hide-filter-bar=true&show-time-filter=true";

        // Add _g parameter with margin date range
        urlSuffix += `&_g=(refreshInterval:(pause:!t,value:0),time:(from:${fromRison},to:${toRison}))`;

        // Check if CUSTOMER type and apply account filters for cur_2_dashboard
        const customerAccountFilter = buildCustomerAccountFilters();
        if (customerAccountFilter) {
          // Add _a parameter WITH customer account filters
          urlSuffix += `&_a=(filters:!(${customerAccountFilter}),timeRestore:!t,viewMode:view)`;
        } else {
          // Add _a parameter without orgCode filter (just timeRestore and viewMode)
          urlSuffix += "&_a=(timeRestore:!t,viewMode:view)";
        }

        URL_SUFFIX = urlSuffix;

        console.log("🔍 Final Cost Insight URL Suffix:", URL_SUFFIX);
      } else {
        // Check if CUSTOMER type - apply account filters even without margin dates for all cost insight dashboards
        const customerAccountFilter = buildCustomerAccountFilters();
        if (customerAccountFilter) {
          // Build custom URL suffix from scratch with customer account filters
          // Use the default time range (now-30d to now) if margin dates not provided
          let urlSuffix =
            "?embed=true&show-top-menu=false&show-query-input=false&hide-filter-bar=true&show-time-filter=true";

          // Add _g parameter with default time range
          urlSuffix += `&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-30d,to:now))`;

          // Add _a parameter WITH customer account filters
          urlSuffix += `&_a=(filters:!(${customerAccountFilter}),timeRestore:!t,viewMode:view)`;

          URL_SUFFIX = urlSuffix;

          console.log(
            "🔍 CUSTOMER Account Filter Applied (no margin dates):",
            URL_SUFFIX
          );
        } else {
          // Original behavior - use default URL suffix
          URL_SUFFIX =
            process.env.REACT_APP_ELASTICSERACH_COST_INSIGHT_URL_SUFFIX;
        }
      }
    } else if (dashboardKey === "RI/SP Dashboard") {
      // Check if CUSTOMER type and apply account filters
      const customerAccountFilter = buildCustomerAccountFilters();
      if (customerAccountFilter) {
        // Build URL suffix with customer account filters
        let urlSuffix =
          "?embed=true&show-query-input=true&show-time-filter=true";
        urlSuffix += `&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-30d,to:now))`;
        urlSuffix += `&_a=(filters:!(${customerAccountFilter}),timeRestore:!t,viewMode:view)`;
        URL_SUFFIX = urlSuffix;

        console.log(
          "🔍 CUSTOMER Account Filter Applied (RI/SP Dashboard):",
          URL_SUFFIX
        );
      } else {
        // Original behavior - use default URL suffix
        URL_SUFFIX =
          "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-30d,to:now))&_a=(timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true";
      }
    } else if (dashboardKey === "App Tagging") {
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-30d,to:now))&_a=(timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true";
    } else if (
      dashboardKey === "Compliance_Trend_Dashboard" ||
      dashboardKey === "Security_Trend_Dashboard"
    ) {
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now/y,to:now))&_a=(timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true";
    } else if (dashboardKey === "cis_waf") {
      console.log("Account Number in Cis_waf :: ", params);
      const fromDate = calculateRelativeTime(params.lastRunDate);

      URL_SUFFIX = `?embed=true&_g=(refreshInterval:(pause:!t,value:60000),time:(from:${fromDate},to:now))&_a=(filters:!((meta:(key:'accountNumber.keyword',params:(query:'${params.accountNumber}'),type:'phrase'),query:(match_phrase:(accountNumber.keyword:'${params.accountNumber}')))),timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true`;
    } else if (
      dashboardKey === "cis_waf_Report" ||
      dashboardKey === "cis_waf_auditor"
    ) {
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-1d,to:now))&_a=(timeRestore:!t,viewMode:view)&show-query-input=true&show-time-filter=true";
    } else if (dashboardKey === "AWS Marketplace – Seller Disbursement Insights" || dashboardKey === "aws_marketplace_offers_insight") {
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-1y,to:now))&_a=(timeRestore:!t,viewMode:view)&show-top-menu=false&show-query-input=false&hide-filter-bar=true&hide-time-filter=true";
    } else if (
      dashboardKey === "AWS Marketplace – Billing" ||
      dashboardKey === "AWS Marketplace – Collection And Disbursment" || 
      dashboardKey === "Marketplace Seller Overview" ||
      dashboardKey === "AWS_Marketplace_others"
    ) {
      URL_SUFFIX =
        "?embed=true&_g=(refreshInterval:(pause:!t,value:0),time:(from:now-1y,to:now))&_a=(timeRestore:!t,viewMode:view)&hide-query-input=true&show-time-filter=true";
    } else if (dashboardKey === "Spend Categorization") {
      // helper: to ISO at start/end of day UTC
      // Convert date string to represent local midnight, then convert to UTC
      const toISOStart = (d) => {
        // Parse date components (YYYY-MM-DD)
        const [year, month, day] = d.split('-').map(Number);
        // Create date in local timezone (month is 0-indexed in JS)
        const dt = new Date(year, month - 1, day, 0, 0, 0, 0);
        // Convert local midnight to UTC ISO string
        return dt.toISOString();
      };
      const toISOEnd = (d) => {
        // Parse date components (YYYY-MM-DD)
        const [year, month, day] = d.split('-').map(Number);
        // Create date in local timezone at end of day (month is 0-indexed in JS)
        const dt = new Date(year, month - 1, day, 23, 59, 59, 999);
        // Convert local end of day to UTC ISO string
        return dt.toISOString();
      };

      // 1) Dates → Use proper Kibana date format (avoid problematic formats)
      const fromRison = params?.startDate
        ? `'${toISOStart(params.startDate)}'`
        : "now-30d";
      const toRison = params?.endDate ? `'${toISOEnd(params.endDate)}'` : "now";

      console.log("🔍 Spend Categorization Debug:", {
        fromRison,
        toRison,
        accountNumbers: params.accountNumbers,
        orgCode: orgcode,
      });

      // 2) Build URL suffix with hidden filters but functional
      let urlSuffix =
        "?embed=true&show-top-menu=false&show-query-input=false&hide-filter-bar=true&hide-time-filter=true";

      // 3) Add _g parameter (no filters, value:0)
      urlSuffix += `&_g=(refreshInterval:(pause:!t,value:0),time:(from:${fromRison},to:${toRison}))`;

      // 4) Add _a parameter with filters (following cis_waf pattern)
      urlSuffix += "&_a=(filters:!(";

      // 5) Add account numbers filter if provided
      if (params?.accountNumbers) {
        const accountNumbersArray = params.accountNumbers
          .split(",")
          .filter((acc) => acc.trim());
        if (accountNumbersArray.length > 0) {
          // For multiple account numbers, use 'should' clause (OR condition)
          if (accountNumbersArray.length === 1) {
            // Single account number
            urlSuffix += `(meta:(key:'line_item_usage_account_id.keyword',params:(query:'${accountNumbersArray[0]}'),type:'phrase'),query:(match_phrase:(line_item_usage_account_id.keyword:'${accountNumbersArray[0]}')))`;
          } else {
            // Multiple account numbers - use bool query with should
            urlSuffix += `(meta:(key:'line_item_usage_account_id.keyword',params:(query:'${accountNumbersArray.join(
              " OR "
            )}'),type:'phrases'),query:(bool:(should:!(`;
            accountNumbersArray.forEach((acc, index) => {
              if (index > 0) urlSuffix += ",";
              urlSuffix += `(match_phrase:(line_item_usage_account_id.keyword:'${acc}'))`;
            });
            urlSuffix += "))))";
          }
        }
      }

      // 6) Add orgCode filter
      if (orgcode) {
        if (params?.accountNumbers) {
          urlSuffix += ",";
        }
        urlSuffix += `(meta:(key:'orgCode.keyword',params:(query:'${orgcode}'),type:'phrase'),query:(match_phrase:(orgCode.keyword:'${orgcode}')))`;
      }

      urlSuffix += "),timeRestore:!t,viewMode:view)";

      URL_SUFFIX = urlSuffix;

      console.log("🔍 Final URL Suffix:", URL_SUFFIX);
    } else {
      URL_SUFFIX = process.env.REACT_APP_ELASTICSERACH_URL_SUFFIX;
    }

    const VIEW_DASHBOARD_URL = `${process.env.REACT_APP_ELASTICSEARCH_VIEW_DASHBOARD_URL}${dashboardId}`;

    const fullURL = `${VIEW_URL}/s/${SPACE_URL}/app/dashboards${QUERY_PARAMS}${VIEW_DASHBOARD_URL}${URL_SUFFIX}`;

    console.log("🔍 Final Full URL Debug:", {
      dashboardKey,
      registrationType: organization?.registrationType,
      orgcode: orgcode,
      hasCustomerAccountNumbers: !!organization?.customerAccountNumbers,
      URL_SUFFIX,
      fullURL: fullURL.substring(0, 200) + "...",
    });

    return fullURL;
  }, [dashboardId, organization, params, dashboardKey]);

  console.log("🚀 ~ dashboardURL ~ dashboardURL:", dashboardURL);

  return useMemo(
    () => ({ dashboardURL, isLoading, errorMessage }),
    [dashboardURL, isLoading, errorMessage]
  );
};
